import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Star, Flame, Award, Zap, Lock, ArrowLeft } from 'lucide-react';
import { getStoredSession } from '../hooks/useAuth';
import { getStudentBadges, type Badge } from '../services/conceptPlus1Service';

const BADGE_CONFIG: Record<string, { icon: typeof Trophy; gradient: string; bg: string; border: string }> = {
  achievement: { icon: Trophy, gradient: 'from-yellow-400 to-orange-500', bg: 'bg-orange-50',  border: 'border-orange-200' },
  mastery:     { icon: Star,   gradient: 'from-blue-500 to-indigo-600',   bg: 'bg-blue-50',    border: 'border-blue-200'   },
  consistency: { icon: Flame,  gradient: 'from-orange-500 to-red-500',    bg: 'bg-red-50',     border: 'border-red-200'    },
  milestone:   { icon: Award,  gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50',   border: 'border-green-200'  },
  performance: { icon: Zap,    gradient: 'from-purple-500 to-pink-500',   bg: 'bg-purple-50',  border: 'border-purple-200' },
};

function getBadgeConfig(badge: Badge) {
  const name = (badge.badge_name || badge.name || '').toLowerCase();
  if (name.includes('gold'))   return { ...BADGE_CONFIG.achievement, gradient: 'from-yellow-400 to-yellow-600', icon: Trophy as typeof Trophy };
  if (name.includes('silver')) return { ...(BADGE_CONFIG.mastery),   gradient: 'from-gray-400 to-gray-600'    };
  if (name.includes('bronze')) return { ...(BADGE_CONFIG.consistency), gradient: 'from-orange-600 to-orange-800' };
  return BADGE_CONFIG[badge.badge_type] ?? BADGE_CONFIG.achievement;
}

function BadgeCard({ badge }: { badge: Badge }) {
  const cfg = getBadgeConfig(badge);
  const Icon = cfg.icon;
  const earned = badge.is_earned;
  const earnedDate = badge.earned_at
    ? new Date(badge.earned_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className={`rounded-2xl border p-5 flex flex-col items-center text-center gap-3 transition-all duration-200 ${
      earned
        ? `${cfg.bg} ${cfg.border} shadow-sm hover:shadow-md hover:-translate-y-0.5`
        : 'bg-gray-50 border-gray-200 opacity-50'
    }`}>
      <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-sm ${
        earned ? `bg-gradient-to-br ${cfg.gradient}` : 'bg-gray-200'
      }`}>
        {earned ? <Icon className="w-8 h-8 text-white" /> : <Lock className="w-7 h-7 text-gray-400" />}
      </div>
      <div>
        <p className={`font-semibold text-sm ${earned ? 'text-ocean-deep' : 'text-gray-400'}`}>
          {badge.badge_name || badge.name}
        </p>
        <p className={`text-xs mt-0.5 leading-snug ${earned ? 'text-gray-600' : 'text-gray-400'}`}>
          {badge.badge_description || badge.description}
        </p>
      </div>
      {earned && earnedDate && (
        <span className="text-[10px] text-gray-500 font-medium">Earned {earnedDate}</span>
      )}
    </div>
  );
}

export default function Badges() {
  const session = getStoredSession();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.student_id) return;
    getStudentBadges(session.student_id)
      .then(setBadges)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.student_id]);

  const earned = badges.filter(b => b.is_earned);
  const locked = badges.filter(b => !b.is_earned);

  return (
    <main className="pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/dashboard" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ocean-deep flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              My Badges
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? 'Loading…' : `${earned.length} earned · ${locked.length} to unlock`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 p-5 h-44 animate-pulse bg-gray-50" />
            ))}
          </div>
        ) : badges.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No badges yet</p>
            <p className="text-sm mt-1">Keep learning to earn your first badge.</p>
          </div>
        ) : (
          <>
            {earned.length > 0 && (
              <section className="mb-10">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Earned ({earned.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {earned.map(b => <BadgeCard key={b.badge_id ?? b.id} badge={b} />)}
                </div>
              </section>
            )}
            {locked.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
                  Locked ({locked.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {locked.map(b => <BadgeCard key={b.badge_id ?? b.id} badge={b} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
